import { trackEvent } from '@/common/utils/telemetry/useTelemetry';

const getElementInfo = (element: Element): { type: string; testId?: string; className?: string } => {
  const tagName = element.tagName?.toLowerCase() || 'unknown';
  const testId = element.getAttribute('data-testid') || undefined;
  const className = element.className || undefined;

  return {
    type: tagName,
    testId,
    className
  };
};

const shouldTrackElement = (element: Element): boolean => {
  if (!element || !element.tagName) {
    return false;
  }

  const excludeSelectors = [
    '[data-no-telemetry]',
    '.no-telemetry',
    '[data-ph-no-capture]',
    '.ph-no-capture',
    '[data-posthog-no-capture]',
    '.posthog-no-capture'
  ];

  const hasExclusionAttribute = excludeSelectors.some(selector => {
    try {
      return element.matches(selector);
    } catch (error) {
      return false;
    }
  });

  return !hasExclusionAttribute;
};

const measureClickDelayToNextFrame = (elementInfo: ReturnType<typeof getElementInfo>, context?: string | null): void => {
  const startTime = performance.now();
  requestAnimationFrame(() => {
    const endTime = performance.now();
    const delay = endTime - startTime;

    if (delay >= 16) {
      trackEvent('click_delay_measured', {
        element_type: elementInfo.type,
        element_testid: elementInfo.testId,
        element_class: elementInfo.className,
        delay_ms: Math.round(delay),
        context
      });
    }
  });
};

const trackClickDelay = (event: Event, context?: string): void => {
  const target = event.target as Element;
  if (!target) {
    return;
  }

  const clickEl = target.getAttribute('data-testid')
    || ['a', 'input', 'button'].includes(target.tagName?.toLowerCase() || '')
      ? target
      : target.closest('a,button,input,[data-testid]');

  if (!clickEl || !shouldTrackElement(clickEl)) {
    return;
  }

  const ctx = context
    || clickEl.closest('[data-testid]')?.getAttribute('data-testid') || '';

  const elementInfo = getElementInfo(clickEl);
  measureClickDelayToNextFrame(elementInfo, ctx);
};

let globalClickListener: ((event: Event) => void) | null = null;

export const initializeClickDelayTracking = (): void => {
  if (globalClickListener) {
    return;
  }

  globalClickListener = (event: Event) => {
    trackClickDelay(event);
  };

  document.addEventListener('click', globalClickListener, {
    capture: true,
    passive: true
  });
};

export const cleanupClickDelayTracking = (): void => {
  if (globalClickListener) {
    document.removeEventListener('click', globalClickListener, { capture: true });
    globalClickListener = null;
  }
};

export { trackClickDelay };
